import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Heart, MessageCircle } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="10144" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-80 animate-pulse rounded-md bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-none shadow-sm transition-all hover:shadow-md hover:-translate-y-1 bg-card/50 backdrop-blur-sm">
          <div className="relative overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-4xl font-serif">SEC</span>
                </div>
              )}
            </AspectRatio>
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Published"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-serif font-bold text-foreground group-hover:text-primary transition-colors">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
              {post.excerpt ? <span dangerouslySetInnerHTML={{ __html: post.excerpt }} /> : "Explore detailed clinical findings and surgical outcomes from our excellence center..."}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between text-primary hover:bg-primary/5 hover:text-primary font-medium">
                View Clinical Detail <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  
  const q = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId)],
    orderby: "date",
    order: "DESC",
  };
  const wp_query = new WP_Query(q);

  return (
    <div className="mt-16">
      <div className="mb-8 flex items-center justify-between border-b border-border pb-4">
        <h3 className="text-2xl font-bold font-serif text-foreground md:text-3xl">
          Clinical <span className="text-primary">Insights</span>
        </h3>
        <Link to="/OtosclerosisPage">
          <Button variant="link" className="hidden text-primary font-semibold md:inline-flex">
            All Publications
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
      <div className="mt-6 text-center md:hidden">
        <Link to="/OtosclerosisPage">
          <Button variant="outline" className="w-full border-primary/20 text-primary font-semibold">
            All Publications
          </Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted/50" />
        <div className="aspect-video w-full animate-pulse rounded-md bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">Failed to load the medical article.</p>
        <Button onClick={refetch} variant="outline" className="border-primary text-primary">Retry Loading</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <p className="text-xl font-medium text-muted-foreground">Article not found.</p>
        <Link to="/HomePage" className="mt-4">
          <Button>Return to Center Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <div className="container mx-auto px-4 py-12 md:py-20">
      <article className="mx-auto max-w-4xl">
        {}
        <header className="mb-12 text-center">
          <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {post.categories && post.categories.length > 0 && (
              <span className="rounded-sm bg-primary/5 px-3 py-1 text-primary border border-primary/10">
                {post.categories[0].name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-primary" />
              {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-primary" />
              Clinical Review
            </span>
          </div>

          <h1 className="mb-8 text-3xl font-bold leading-tight font-serif text-foreground md:text-5xl lg:text-6xl tracking-tight">
            {post.title}
          </h1>

          {}
          {post.author && (
            <div className="flex items-center justify-center gap-4">
              <Avatar className="h-12 w-12 border border-border shadow-sm">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback className="bg-primary/5 text-primary"><User className="h-6 w-6" /></AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="text-sm font-bold text-foreground">Authored by {post.author.name}</p>
                <p className="text-xs text-muted-foreground">Stapes Excellence Center Faculty</p>
              </div>
            </div>
          )}
        </header>

        {}
        <div className="mb-16 overflow-hidden rounded-md shadow-xl ring-1 ring-border/50">
          <AspectRatio ratio={16 / 9}>
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/5 to-muted">
                <span className="text-6xl font-serif text-primary/20">SEC</span>
              </div>
            )}
          </AspectRatio>
        </div>

        {}
        <div className="prose prose-lg prose-headings:font-serif prose-headings:text-foreground prose-headings:font-bold prose-p:text-muted-foreground prose-p:leading-relaxed prose-a:text-primary prose-a:font-semibold prose-img:rounded-md max-w-none">
          {post.content ? (
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          ) : (
            <p>No medical content available for this publication.</p>
          )}
        </div>

        {}
        <div className="mt-16 flex flex-col gap-6 border-y border-border py-10 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-3">
            {post.tags && post.tags.map((tag, i) => (
              <span key={i} data-index={i} className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                #{tag.name}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold uppercase tracking-widest text-foreground">Share Article:</span>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-md border-border hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all">
              <Share2 className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-md border-border hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all">
              <Heart className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-md border-border hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-all">
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {}
        {post.author && (
          <div className="mt-16 rounded-md bg-card p-8 border border-border/50 shadow-sm">
            <div className="flex flex-col items-center gap-8 text-center md:flex-row md:text-left">
              <Avatar className="h-28 w-28 border-2 border-background shadow-md">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback className="text-3xl bg-primary/5 text-primary font-serif"><User /></AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-3">
                <h3 className="text-2xl font-bold font-serif text-foreground">About {post.author.name}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Leading the field in stapes surgery and otological innovation. Dedicated to restoring hearing through pioneering surgical techniques and clinical excellence at the Stapes Excellence Center.
                </p>
                <Link to={post.author.link || "#"}>
                  <Button variant="link" className="h-auto p-0 text-primary font-bold uppercase tracking-wider text-xs">
                    View Medical Credentials
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {}
        <RelatedPostsSection currentPostId={post.id} />
      </article>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const current_wp_query_params = wvcClient.getCurrentWPQueryParams();
  
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-post-template-section" className="relative min-h-screen w-full bg-background">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[10%] -right-[5%] h-[500px] w-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[30%] -left-[5%] h-[400px] w-[400px] rounded-full bg-accent/30 blur-[100px]" />
      </div>

      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}